import React, { useState, useEffect } from 'react';
import { Target, Compass, TrendingUp, ChevronRight, X, Loader2 } from 'lucide-react';

const MotivationModal = ({ onComplete, onSkip, existingMotivation }) => {
  const [aspirations, setAspirations] = useState('');
  const [goals, setGoals] = useState('');
  const [growthAreas, setGrowthAreas] = useState('');
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (existingMotivation) {
      setAspirations(existingMotivation.aspirations || '');
      setGoals(existingMotivation.goals || '');
      setGrowthAreas(existingMotivation.growth_areas || '');
    }
  }, [existingMotivation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/auth/motivation`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          aspirations: aspirations || null,
          goals: goals || null,
          growth_areas: growthAreas || null,
        }),
      });
      if (resp.ok) {
        onComplete();
      }
    } catch (err) {
      console.error('Failed to save motivation:', err);
    } finally {
      setSaving(false);
    }
  };

  const hasContent = aspirations.trim() || goals.trim() || growthAreas.trim();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="motivation-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid="motivation-skip-x"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white mb-2">
            What matters to you?
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed">
            The more your companion understands about where you're heading, the better it can support you.
            Take a moment to share what's on your mind — you can always come back and update this.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 flex items-center gap-2">
              <Compass className="w-4 h-4 text-blue-400" />
              What would you like to achieve?
            </label>
            <textarea
              value={aspirations}
              onChange={(e) => setAspirations(e.target.value)}
              placeholder="e.g. Build confidence leading a larger team, transition into a senior role, develop a product that makes a difference..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
              rows={3}
              data-testid="motivation-aspirations-input"
            />
          </div>

          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-green-400" />
              What are your goals right now?
            </label>
            <textarea
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              placeholder="e.g. Have better one-to-ones with my team, get promoted this year, launch my side project, improve work-life balance..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
              rows={3}
              data-testid="motivation-goals-input"
            />
          </div>

          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              What do you believe you could do better?
            </label>
            <textarea
              value={growthAreas}
              onChange={(e) => setGrowthAreas(e.target.value)}
              placeholder="e.g. Delegating instead of doing everything myself, having difficult conversations sooner, making decisions with less hesitation..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none transition-colors resize-none"
              rows={3}
              data-testid="motivation-growth-input"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
              data-testid="motivation-save-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              {saving ? 'Saving...' : hasContent ? 'Save & Continue' : 'Continue'}
              {!saving && <ChevronRight className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-2.5 text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
              data-testid="motivation-skip-button"
            >
              I'll think about it
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MotivationModal;
